import { useState } from "react";
import { useAutomation, RecurringType, RecurringFrequency } from "@/contexts/AutomationContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  Plus, Play, Pause, Trash2, Zap, CalendarClock, RotateCcw, Tag, RefreshCw,
  FileText, Wallet, CreditCard, ArrowRight
} from "lucide-react";

const CATEGORIES = ["Office", "Travel", "Utilities", "Salary", "Marketing", "Miscellaneous", "Sales"];
const PAYMENT_METHODS = ["Bank Transfer", "UPI", "Cash", "Credit Card", "Debit Card", "Cheque"];
const TYPE_LABELS: Record<RecurringType, string> = { invoice: "Invoice", expense: "Expense", payment: "Payment" };
const FREQ_LABELS: Record<RecurringFrequency, string> = { weekly: "Weekly", monthly: "Monthly", quarterly: "Quarterly", yearly: "Yearly" };

const fmt = (n: number) => `₹${n.toLocaleString("en-IN")}`;

export default function AutomationPage() {
  const {
    recurringEntries, generatedEntries, categoryRules,
    addRecurring, toggleRecurringStatus, deleteRecurring, runSchedule, runAllDue,
    addCategoryRule, deleteCategoryRule, autoCategorizeText, getDueEntries,
  } = useAutomation();
  const { toast } = useToast();
  const [tab, setTab] = useState("recurring");

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [formType, setFormType] = useState<RecurringType>("expense");
  const [formTitle, setFormTitle] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formAmount, setFormAmount] = useState("");
  const [formFreq, setFormFreq] = useState<RecurringFrequency>("monthly");
  const [formCategory, setFormCategory] = useState("Office");
  const [formVendor, setFormVendor] = useState("");
  const [formPayment, setFormPayment] = useState("Bank Transfer");
  const [formStartDate, setFormStartDate] = useState(new Date().toISOString().split("T")[0]);

  // Rule form
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [ruleKeyword, setRuleKeyword] = useState("");
  const [ruleCategory, setRuleCategory] = useState("Office");
  const [ruleVendor, setRuleVendor] = useState("");
  const [rulePayment, setRulePayment] = useState("");

  // Auto-categorize test
  const [testText, setTestText] = useState("");
  const testResult = testText ? autoCategorizeText(testText) : null;

  const dueCount = getDueEntries().length;
  const totalMonthly = recurringEntries
    .filter((e) => e.status === "Active")
    .reduce((s, e) => {
      const multiplier = e.frequency === "weekly" ? 4.33 : e.frequency === "monthly" ? 1 : e.frequency === "quarterly" ? 1 / 3 : 1 / 12;
      return s + e.amount * multiplier;
    }, 0);

  const handleAddRecurring = () => {
    if (!formTitle || !formAmount) return;
    addRecurring({
      type: formType, title: formTitle, description: formDesc,
      amount: parseFloat(formAmount), frequency: formFreq,
      category: formCategory, vendor: formVendor || undefined,
      paymentMethod: formPayment, startDate: formStartDate, status: "Active",
    });
    toast({ title: "Schedule Created", description: `"${formTitle}" added as ${FREQ_LABELS[formFreq].toLowerCase()} ${TYPE_LABELS[formType].toLowerCase()}` });
    setShowForm(false);
    resetForm();
  };

  const resetForm = () => {
    setFormTitle(""); setFormDesc(""); setFormAmount(""); setFormVendor("");
    setFormType("expense"); setFormFreq("monthly"); setFormCategory("Office");
    setFormPayment("Bank Transfer"); setFormStartDate(new Date().toISOString().split("T")[0]);
  };

  const handleRunAll = () => {
    const results = runAllDue();
    toast({
      title: results.length > 0 ? `${results.length} Entries Generated` : "No Due Entries",
      description: results.length > 0 ? `Generated entries worth ${fmt(results.reduce((s, r) => s + r.amount, 0))}` : "All schedules are up to date",
    });
  };

  const handleAddRule = () => {
    if (!ruleKeyword) return;
    addCategoryRule({ keyword: ruleKeyword, category: ruleCategory, vendor: ruleVendor || undefined, paymentMethod: rulePayment || undefined });
    toast({ title: "Rule Added", description: `Keyword "${ruleKeyword}" → ${ruleCategory}` });
    setShowRuleForm(false);
    setRuleKeyword(""); setRuleVendor(""); setRulePayment("");
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">Automation</h1>
          <p className="text-sm text-muted-foreground">Recurring entries, auto-categorization & scheduled transactions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRunAll} className="gap-2">
            <RefreshCw className="h-4 w-4" /> Run Due ({dueCount})
          </Button>
          <Dialog open={showForm} onOpenChange={setShowForm}>
            <DialogTrigger asChild>
              <Button className="gap-2"><Plus className="h-4 w-4" /> New Schedule</Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Create Recurring Schedule</DialogTitle></DialogHeader>
              <div className="grid gap-4 py-2">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Type</Label>
                    <Select value={formType} onValueChange={(v) => setFormType(v as RecurringType)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="expense">Expense</SelectItem>
                        <SelectItem value="invoice">Invoice</SelectItem>
                        <SelectItem value="payment">Payment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Frequency</Label>
                    <Select value={formFreq} onValueChange={(v) => setFormFreq(v as RecurringFrequency)}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Title</Label>
                  <Input value={formTitle} onChange={(e) => setFormTitle(e.target.value)} placeholder="e.g. Monthly Office Rent" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)} placeholder="Details..." rows={2} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Amount (₹)</Label>
                    <Input type="number" value={formAmount} onChange={(e) => setFormAmount(e.target.value)} placeholder="0" />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={formCategory} onValueChange={setFormCategory}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Vendor</Label>
                    <Input value={formVendor} onChange={(e) => setFormVendor(e.target.value)} placeholder="Optional" />
                  </div>
                  <div>
                    <Label>Payment Method</Label>
                    <Select value={formPayment} onValueChange={setFormPayment}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{PAYMENT_METHODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Start Date</Label>
                  <Input type="date" value={formStartDate} onChange={(e) => setFormStartDate(e.target.value)} />
                </div>
                <Button onClick={handleAddRecurring} className="w-full gap-2"><CalendarClock className="h-4 w-4" /> Create Schedule</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2"><CardDescription>Active Schedules</CardDescription></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{recurringEntries.filter((e) => e.status === "Active").length}</div>
            <p className="text-xs text-muted-foreground">{recurringEntries.filter((e) => e.status === "Paused").length} paused</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Est. Monthly Outflow</CardDescription></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{fmt(Math.round(totalMonthly))}</div>
            <p className="text-xs text-muted-foreground">Across all recurring</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Total Generated</CardDescription></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{generatedEntries.length}</div>
            <p className="text-xs text-muted-foreground">{generatedEntries.filter((g) => g.status === "Posted").length} posted</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2"><CardDescription>Auto-Category Rules</CardDescription></CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-foreground">{categoryRules.length}</div>
            <p className="text-xs text-muted-foreground">Active rules</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="recurring" className="gap-1.5"><CalendarClock className="h-3.5 w-3.5" /> Recurring</TabsTrigger>
          <TabsTrigger value="generated" className="gap-1.5"><FileText className="h-3.5 w-3.5" /> Generated</TabsTrigger>
          <TabsTrigger value="rules" className="gap-1.5"><Tag className="h-3.5 w-3.5" /> Auto-Categorize</TabsTrigger>
        </TabsList>

        {/* Recurring Entries Tab */}
        <TabsContent value="recurring">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Recurring Schedules</CardTitle>
              <CardDescription>Automated invoices, expenses & payments</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Frequency</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Next Run</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recurringEntries.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-foreground">{entry.title}</p>
                          {entry.vendor && <p className="text-xs text-muted-foreground">{entry.vendor}</p>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{TYPE_LABELS[entry.type]}</Badge>
                      </TableCell>
                      <TableCell className="font-mono font-medium">{fmt(entry.amount)}</TableCell>
                      <TableCell>{FREQ_LABELS[entry.frequency]}</TableCell>
                      <TableCell><Badge variant="secondary">{entry.category}</Badge></TableCell>
                      <TableCell className="text-sm">{entry.nextRunDate}</TableCell>
                      <TableCell>
                        <Badge variant={entry.status === "Active" ? "default" : "secondary"}>
                          {entry.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">{entry.totalGenerated}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => {
                              const gen = runSchedule(entry.id);
                              if (gen) toast({ title: "Entry Generated", description: `${entry.title} — ${fmt(entry.amount)}` });
                            }}
                            disabled={entry.status !== "Active"}
                          >
                            <Play className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8"
                            onClick={() => toggleRecurringStatus(entry.id)}
                          >
                            {entry.status === "Active" ? <Pause className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
                          </Button>
                          <Button
                            variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                            onClick={() => { deleteRecurring(entry.id); toast({ title: "Deleted", description: entry.title }); }}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {recurringEntries.length === 0 && (
                    <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">No recurring schedules yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Generated Entries Tab */}
        <TabsContent value="generated">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Generated Entries</CardTitle>
              <CardDescription>Auto-generated transactions from schedules</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Schedule</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Vendor</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {generatedEntries.map((gen) => (
                    <TableRow key={gen.id}>
                      <TableCell className="text-sm">{gen.generatedDate}</TableCell>
                      <TableCell className="font-medium text-foreground">{gen.recurringTitle}</TableCell>
                      <TableCell><Badge variant="outline" className="capitalize">{TYPE_LABELS[gen.type]}</Badge></TableCell>
                      <TableCell className="font-mono font-medium">{fmt(gen.amount)}</TableCell>
                      <TableCell><Badge variant="secondary">{gen.category}</Badge></TableCell>
                      <TableCell className="text-sm text-muted-foreground">{gen.vendor || "—"}</TableCell>
                      <TableCell>
                        <Badge variant={gen.status === "Posted" ? "default" : gen.status === "Generated" ? "secondary" : "destructive"}>
                          {gen.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                  {generatedEntries.length === 0 && (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No generated entries yet</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Auto-Categorize Tab */}
        <TabsContent value="rules">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-base">Category Rules</CardTitle>
                    <CardDescription>Auto-assign category, vendor & payment method by keyword</CardDescription>
                  </div>
                  <Dialog open={showRuleForm} onOpenChange={setShowRuleForm}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="gap-1.5"><Plus className="h-3.5 w-3.5" /> Add Rule</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>New Auto-Category Rule</DialogTitle></DialogHeader>
                      <div className="grid gap-3 py-2">
                        <div>
                          <Label>Keyword (matched in description)</Label>
                          <Input value={ruleKeyword} onChange={(e) => setRuleKeyword(e.target.value)} placeholder="e.g. rent, google, salary" />
                        </div>
                        <div>
                          <Label>Category</Label>
                          <Select value={ruleCategory} onValueChange={setRuleCategory}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Vendor (optional)</Label>
                          <Input value={ruleVendor} onChange={(e) => setRuleVendor(e.target.value)} placeholder="Auto-assign vendor" />
                        </div>
                        <div>
                          <Label>Payment Method (optional)</Label>
                          <Select value={rulePayment} onValueChange={setRulePayment}>
                            <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="">None</SelectItem>
                              {PAYMENT_METHODS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button onClick={handleAddRule} className="w-full gap-2"><Tag className="h-4 w-4" /> Add Rule</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Keyword</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead className="text-right">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categoryRules.map((rule) => (
                        <TableRow key={rule.id}>
                          <TableCell>
                            <code className="text-xs bg-muted px-2 py-0.5 rounded">{rule.keyword}</code>
                          </TableCell>
                          <TableCell><Badge variant="secondary">{rule.category}</Badge></TableCell>
                          <TableCell className="text-sm">{rule.vendor || "—"}</TableCell>
                          <TableCell className="text-sm">{rule.paymentMethod || "—"}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => deleteCategoryRule(rule.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            {/* Test auto-categorize */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2"><Zap className="h-4 w-4" /> Test Categorizer</CardTitle>
                <CardDescription>Type a transaction description to test rules</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Transaction Description</Label>
                  <Textarea value={testText} onChange={(e) => setTestText(e.target.value)} placeholder="e.g. Google Workspace monthly subscription" rows={3} />
                </div>
                {testText && (
                  <div className="space-y-2 p-3 rounded-lg bg-muted/50 border">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Result</p>
                    {testResult?.category ? (
                      <>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Category:</span>
                          <Badge>{testResult.category}</Badge>
                        </div>
                        {testResult.vendor && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Vendor:</span>
                            <span className="text-sm font-medium text-foreground">{testResult.vendor}</span>
                          </div>
                        )}
                        {testResult.paymentMethod && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Payment:</span>
                            <span className="text-sm text-foreground">{testResult.paymentMethod}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">No matching rule found</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
